/*eslint arrow-body-style: [0]*/
import { Box } from "@mui/system";
import React, { useMemo, useState } from "react";
import { Icon } from "@iconify/react";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";
import _, { first } from "lodash";
import {
  ButtonPrimary,
  ButtonPrimary1,
  HStack,
  VStack,
} from "../../components/utils";
import {
  CheckBoxWithLabel,
  Input,
  Label,
  TextArea,
} from "../../components/shared/InputField";
import {
  useEsopPlans,
  useGetAllCompanyConfigs,
  useGetConfigDetails,
  usesyncHrms,
  useTestHrmsConfig,
  useVestingTemplates,
} from "../../queries";
import { useValuationDetails } from "../../queries/Valuation";
import { Select } from "../../components/shared/Select";
import {
  formatCurrency,
  getCurrencySymbol,
  getCurrencyType,
} from "../../utils/currencyFormatter";
import { useCompanyStore } from "../../store";
import {
  EmployeeStateOptions,
  StatusOptions,
  SyncSettings,
} from "../../types/common";
import { MultiSelectV2 } from "../../components/shared/MultiSelectV2";
import { EmployeeStates } from "../settings/EmployeeDatasourceSettings";
import Tooltip from "../../components/shared/Tooltip";
import { trimTrailingAndLeadingSpaces } from "../../utils/string";
import InfoBox from "../../components/shared/InfoBox";

type DarwinBoxDatasetConfig = {
  username: string;
  password: string;
  apiKey: string;
  datasetKey: string;
  domain: string;
  esopPlanId?: string;
  vestingScheduleId?: string;
  valuationId?: string;
};
function DarwinBoxDatasetImport(props: { resetData: () => void }) {
  const { resetData } = props;
  const navigate = useNavigate();
  const currency = getCurrencyType();
  const { data: existingConfigs } = useGetAllCompanyConfigs();
  const [consentGiven, setConsentGiven] = useState(false);
  const [credentialsValid, setCredentialsValid] = useState(false);
  const [operationInProgress, setOperationInProgress] = useState(false);
  const { data: planData } = useEsopPlans();
  const { data: vestingSchedules } = useVestingTemplates();
  const { data: valuations } = useValuationDetails();
  const { mutate: testDarwinBoxConfig } = useTestHrmsConfig();
  const { mutate: syncDarwinBox } = usesyncHrms();
  const { companyData } = useCompanyStore();
  const [hasGrantInformation, setHasGrantInformation] = useState(false);
  const name = useMemo(() => {
    return `Darwinbox Integration ${companyData?.name || ""} ${
      (existingConfigs?.length || 0) + 1
    }`;
  }, [existingConfigs, companyData]);
  const [darwinboxData, setDarwinboxData] = useState<{
    type: string;
    credentialData: DarwinBoxDatasetConfig;
    settings?: SyncSettings;
    name?: string;
    createNewConfig?: boolean;
    id?: string;
  }>({
    name,
    type: "DarwinboxDataset",
    credentialData: {
      apiKey: "",
      datasetKey: "",
      domain: "",
      password: "",
      username: "",
    },
    settings: {
      grantType: "ESOP",
      syncFrequency: 1,
      dataToSync: ["all"],
      considerResignationDateForLapsing: false,
      additionalSettings: {},
      syncMode: "AUTOMATIC",
      planForGrant: "",
      valuationForGrant: "",
      autoTransferToPersonalEmail: false,
      grantIdentifierTemplate: "",
      strikePriceForGrant: "",
      vestingScheduleForGrant: "",
      requireApprovalForResignation: false,
      autoRaiseRequestForInternalTransfer: false,
      internalTransferMatchFields: ["name"],
    },
    createNewConfig: true,
  });
  const newEmployeeFilters = useMemo(() => {
    return (
      darwinboxData.settings?.additionalSettings?.employeeImportFilter?.new
        ?.status || []
    );
  }, [darwinboxData.settings?.additionalSettings?.employeeImportFilter]);
  const exampleTemplate = useMemo(() => {
    if (!darwinboxData?.settings?.grantIdentifierTemplate) return "";
    const hrId = "E1_1224";
    const grantLength = Math.max(_.random(), 1);
    try {
      const identifier = _.template(
        darwinboxData?.settings?.grantIdentifierTemplate
      )({
        hrId,
        grantLength,
      });
      return identifier;
    } catch (e) {
      return "";
    }
  }, [darwinboxData?.settings?.grantIdentifierTemplate]);
  const testCredentials = () => {
    setOperationInProgress(true);
    const credentialData = darwinboxData.credentialData;
    if (
      !credentialData.apiKey ||
      !credentialData.datasetKey ||
      !credentialData.password ||
      !credentialData.username
    ) {
      toast("Please fill all the fields", { autoClose: 2000, type: "error" });
      setOperationInProgress(false);
      return;
    }
    const darwinboxTestData = trimTrailingAndLeadingSpaces(darwinboxData);
    testDarwinBoxConfig(darwinboxTestData, {
      onSuccess: (data: { hasGrantInformation: boolean; syncId: string }) => {
        if (data.hasGrantInformation) {
          toast(
            "Data has Grant related information, please select a Plan, Vesting Schedule and a Valuation",
            {
              type: "info",
              autoClose: 3000,
            }
          );
          setHasGrantInformation(true);
          setDarwinboxData((prev) => ({
            ...prev,
            id: data.syncId,
          }));
        }
        setCredentialsValid(true);
        toast("Credentials are valid, proceed to import", {
          type: "success",
          autoClose: 3000,
        });
        setOperationInProgress(false);
      },
      onError: () => {
        setCredentialsValid(false);
        toast("Credentials are Invalid, please check again", {
          type: "error",
          autoClose: 3000,
        });
        setOperationInProgress(false);
      },
    });
  };
  const importData = () => {
    if (hasGrantInformation) {
      if (
        !darwinboxData.settings?.planForGrant ||
        !darwinboxData.settings?.vestingScheduleForGrant ||
        !darwinboxData.settings?.valuationForGrant
      ) {
        toast("Please fill all the Grant related fields", {
          autoClose: 2000,
          type: "error",
        });
        return;
      }
    }
    const darwinboxImportData = trimTrailingAndLeadingSpaces(darwinboxData);
    setOperationInProgress(true);
    syncDarwinBox(darwinboxImportData, {
      onSuccess: () => {
        toast("Successfully imported records, you'll now be redirected", {
          type: "success",
          autoClose: 3000,
        });
        setTimeout(() => {
          navigate("/options/allEmployees");
        }, 2000);
        setOperationInProgress(false);
      },
      onError: () => {
        toast("Encountered error while importing", {
          type: "error",
          autoClose: 3000,
        });
        setOperationInProgress(false);
      },
    });
  };
  const handleConfigChange = (
    field: keyof DarwinBoxDatasetConfig | keyof SyncSettings,
    value: string | object | number | boolean
  ) => {
    const settingFields = [
      "planForGrant",
      "valuationForGrant",
      "grantType",
      "vestingScheduleForGrant",
      "strikePriceForGrant",
      "grantIdentifierTemplate",
      "syncMode",
      "additionalSettings",
      "syncFrequency",
      "dataToSync",
    ];
    if (settingFields.includes(field)) {
      if (field === "additionalSettings" && typeof value === "object") {
        const newAdditionalSettings = {
          ...darwinboxData.settings?.additionalSettings,
          ...value,
        };
        setDarwinboxData((prevConfig) => ({
          ...prevConfig,
          settings: {
            ...prevConfig.settings!,
            additionalSettings: newAdditionalSettings,
          },
        }));
        return;
      }
      setDarwinboxData((prevConfig) => ({
        ...prevConfig,
        settings: {
          ...prevConfig.settings!,
          [field]: value,
        },
      }));
      return;
    }
    setDarwinboxData((prevConfig) => ({
      ...prevConfig,
      credentialData: {
        ...prevConfig.credentialData,
        [field]: value,
      },
    }));
  };
  return (
    <HStack key="darwinbox-import">
      <VStack className="justify-between w-full">
        <Box className="justify-start w-full ">
          <h6 className="flex justify-start row text-lg font-medium pb-1">
            Enter Your Darwinbox Credentials
          </h6>
          <p className="text-xxs">
            Enter your Dataset details to pull data from darwinbox
          </p>
          <p className="text-xxs pt-2">
            Please ensure that the credentials provided have sufficient access
            to read Employee and Grant related data. This integration only pulls
            data using (/masterapi/employee) endpoint and does not modify any
            data on your Darwinbox account.
          </p>
        </Box>
        <HStack className="gap-8 pt-8">
          <div className="flex-1">
            <Label className="text-sm font-normal"> Integration name</Label>
            <Input
              value={darwinboxData?.name}
              placeholder="Enter a name"
              onChange={(e) =>
                setDarwinboxData((prev) => ({
                  ...prev,
                  name: e.target.value,
                }))
              }
            />
          </div>
        </HStack>
        <HStack className="gap-8 pt-8 border-t-2 mt-6">
          <VStack className="w-full gap-4">
            <HStack className="justify-start text-lg font-medium pb-1 w-full">
              Darwinbox Credentials
            </HStack>
            <HStack className="gap-4">
              <div className="flex-1">
                <Label className="text-sm font-normal">
                  {" "}
                  Host Name (*.darwinbox.in)
                </Label>
                <Input
                  autoComplete="false"
                  type="text"
                  required
                  disabled={credentialsValid}
                  name=""
                  value={darwinboxData.credentialData?.domain}
                  placeholder="eg : *.darwinbox.in"
                  onChange={(e) => handleConfigChange("domain", e.target.value)}
                />
              </div>
              <div className="flex-1">
                <Label className="text-sm font-normal">API Username</Label>
                <Input
                  type="text"
                  name="username"
                  required
                  value={darwinboxData.credentialData?.username}
                  disabled={credentialsValid}
                  onChange={(e) =>
                    handleConfigChange("username", e.target.value)
                  }
                />
              </div>
              <div className="flex-1">
                <Label className="text-sm font-normal">API Password</Label>
                <Input
                  type="password"
                  name="api_password"
                  required
                  disabled={credentialsValid}
                  value={darwinboxData.credentialData?.password}
                  onChange={(e) =>
                    handleConfigChange("password", e.target.value)
                  }
                />
              </div>
            </HStack>
          </VStack>
        </HStack>
        <HStack className="gap-8 pt-8">
          <div className="flex-1">
            <Label className="text-sm font-normal">API Key</Label>
            <TextArea
              name="api_key"
              required
              disabled={credentialsValid}
              value={darwinboxData.credentialData?.apiKey}
              onChange={(e) => handleConfigChange("apiKey", e.target.value)}
            />
          </div>
          <div className="flex-1">
            <Label className="text-sm font-normal">Dataset Key</Label>
            <TextArea
              name="datasetKey"
              required
              disabled={credentialsValid}
              value={darwinboxData.credentialData?.datasetKey}
              onChange={(e) => handleConfigChange("datasetKey", e.target.value)}
            />
          </div>
        </HStack>
        {hasGrantInformation && (
          <HStack className="gap-8 pt-8 border-t-2 mt-6 w-full">
            <VStack className="w-full pt-4">
              <HStack className="justify-start text-lg font-medium pb-1 w-full">
                Grant Settings
              </HStack>
              <HStack className="w-full gap-4 justify-around">
                <div className="flex-1">
                  <Label className="text-sm font-normal">Plan Name</Label>
                  <Select
                    name="esopPlanId"
                    required
                    value={darwinboxData.settings?.planForGrant}
                    onChange={(e) =>
                      handleConfigChange("planForGrant", e.target.value)
                    }
                    options={
                      planData?.map((plan) => {
                        return { id: plan.esopPlanId, name: plan.planName };
                      }) || []
                    }
                    textGetter={(e) => e.name}
                    valueGetter={(e) => e.id}
                  />
                </div>
                <div className="flex-1">
                  <Label className="text-sm font-normal">
                    Vesting Schedule
                  </Label>
                  <Select
                    name="vestingScheduleId"
                    required
                    value={darwinboxData.credentialData?.vestingScheduleId}
                    onChange={(e) =>
                      handleConfigChange(
                        "vestingScheduleForGrant",
                        e.target.value
                      )
                    }
                    options={
                      vestingSchedules?.map((schedule) => {
                        return {
                          id: schedule.id,
                          name: schedule.vestingTemplateName,
                        };
                      }) || []
                    }
                    textGetter={(e) => e.name}
                    valueGetter={(e) => e.id}
                  />
                </div>
                <div className="flex-1">
                  <Label className="text-sm font-normal">Valuation</Label>
                  <Select
                    name="valuationForGrant"
                    required
                    value={darwinboxData?.settings?.valuationForGrant}
                    onChange={(e) =>
                      handleConfigChange("valuationForGrant", e.target.value)
                    }
                    options={
                      valuations?.map((valuation) => {
                        return {
                          id: valuation.id,
                          name: `${valuation.name} -  PPS: ${formatCurrency(
                            valuation.pps,
                            currency
                          )}`,
                        };
                      }) || []
                    }
                    textGetter={(e) => e.name}
                    valueGetter={(e) => e.id}
                  />
                </div>
              </HStack>
              <HStack className="w-full gap-4 justify-around mt-4">
                <div className="flex-1">
                  <Label className="text-sm font-normal">
                    Strike Price ({companyData?.currency})
                  </Label>
                  <Input
                    name="strikePriceForGrant"
                    required
                    value={darwinboxData?.settings?.strikePriceForGrant}
                    onChange={(e) =>
                      handleConfigChange("strikePriceForGrant", e.target.value)
                    }
                  />
                </div>
                <div className="flex-1">
                  <Label className="text-sm font-normal">Grant Type</Label>
                  <Input
                    name="grantType"
                    required
                    value={darwinboxData?.settings?.grantType}
                    onChange={(e) =>
                      handleConfigChange("grantType", e.target.value)
                    }
                  />
                </div>
                <div className="flex-1">
                  <Label className="text-sm font-normal">
                    Grant Identifier Template
                  </Label>
                  <HStack>
                    <Input
                      name="grantIdentifierTemplate"
                      required
                      value={darwinboxData?.settings?.grantIdentifierTemplate}
                      onChange={(e) =>
                        handleConfigChange(
                          "grantIdentifierTemplate",
                          e.target.value
                        )
                      }
                    />
                  </HStack>
                  {darwinboxData?.settings?.grantIdentifierTemplate && (
                    <HStack className="justify-start text-xs mt-1 gap-2">
                      Example:
                      <span className="text-blue-500">{exampleTemplate}</span>
                    </HStack>
                  )}
                </div>
              </HStack>
              <HStack className="w-full gap-4 justify-start mt-4">
                <InfoBox message="Grant(s) will be created only when the employee is in 'PENDING' status and will have Employee Joining date as the Grant Date & Vesting Start Date" />
              </HStack>
            </VStack>
          </HStack>
        )}
        {credentialsValid && (
          <HStack className="gap-8 pt-8 border-t-2 mt-6">
            <VStack className="w-full">
              <HStack className="flex justify-start text-lg font-medium pb-1">
                Sync Settings
              </HStack>
              <HStack className="w-full gap-4 justify-around">
                <div className="flex-1">
                  <Label className="text-sm font-normal">Sync Mode</Label>
                  <Select
                    name="syncMode"
                    required
                    value={darwinboxData.settings?.syncMode}
                    onChange={(e) =>
                      handleConfigChange("syncMode", e.target.value)
                    }
                    options={[
                      { id: "AUTOMATIC", name: "AUTOMATIC" },
                      { id: "MANUAL", name: "MANUAL" },
                    ]}
                    textGetter={(e) => e.name}
                    valueGetter={(e) => e.id}
                  />
                </div>
                <div className="flex-1">
                  <Label className="text-sm font-normal">
                    Sync Frequency (in days)
                  </Label>
                  <Input
                    type="number"
                    min={1}
                    name="syncFrequency"
                    required
                    value={darwinboxData.settings?.syncFrequency}
                    onChange={(e) =>
                      handleConfigChange(
                        "syncFrequency",
                        Number(e.target.value)
                      )
                    }
                  />
                </div>
                <div className="flex-1">
                  <Label className="text-sm font-normal">Data to Sync</Label>
                  <MultiSelectV2
                    _className="p-2 border rounded max-h-16 overflow-y-auto"
                    multiple={true}
                    placeholder={"--Select--"}
                    options={EmployeeStateOptions}
                    handleSelection={(e) => {
                      handleConfigChange("dataToSync", e);
                    }}
                    optionValues={darwinboxData.settings?.dataToSync || []}
                  />
                </div>
              </HStack>
              <HStack className="w-1/2">
                <VStack>
                  <div className="flex-1">
                    {(darwinboxData.settings?.dataToSync.includes(
                      EmployeeStates.NEW_EMPLOYEES
                    ) ||
                      darwinboxData.settings?.dataToSync.includes(
                        EmployeeStates.ALL
                      )) && (
                      <>
                        <Label className="text-sm font-normal inline-flex gap-2">
                          New Employee Status Filter
                          <Tooltip
                            _className="w-[200px]"
                            text="What all states to consider while creating a new Employee"
                          >
                            <Icon
                              icon="material-symbols:info-outline"
                              width={24}
                            />
                          </Tooltip>
                        </Label>
                        <MultiSelectV2
                          _className="p-2 border rounded w-full min-w-[500px] max-h-40 overflow-y-auto"
                          multiple={true}
                          placeholder={"--Select--"}
                          options={StatusOptions}
                          handleSelection={(e) => {
                            const newFilter = {
                              new: {
                                status: e,
                              },
                            };
                            handleConfigChange("additionalSettings", {
                              employeeImportFilter: newFilter,
                            });
                          }}
                          optionValues={newEmployeeFilters}
                        />
                      </>
                    )}
                  </div>
                </VStack>
              </HStack>
            </VStack>
          </HStack>
        )}
        <HStack className="mt-8">
          <CheckBoxWithLabel
            label="By checking this box, I authorize HISSA to access my Employee data from Darwinbox."
            labelclassname={"ml-2"}
            onChange={(e) => setConsentGiven(e.target.checked)}
          />
        </HStack>
        <HStack className="justify-end gap-4 p-3">
          <ButtonPrimary1
            type="reset"
            className="text-red-500"
            onClick={() => {
              resetData();
            }}
          >
            Cancel
          </ButtonPrimary1>
          {operationInProgress ? (
            <Icon
              className="animate-spin text-orange-501"
              icon="lucide:loader-2"
              width={36}
            />
          ) : (
            <>
              {!credentialsValid ? (
                <ButtonPrimary
                  type="reset"
                  disabled={!consentGiven}
                  className="text-red-500"
                  onClick={() => {
                    testCredentials();
                  }}
                >
                  Test
                </ButtonPrimary>
              ) : (
                <ButtonPrimary
                  type="reset"
                  disabled={!consentGiven}
                  className="text-red-500"
                  onClick={() => {
                    importData();
                  }}
                >
                  Import
                </ButtonPrimary>
              )}
            </>
          )}
        </HStack>
      </VStack>
    </HStack>
  );
}

export default DarwinBoxDatasetImport;
